import { memo, useMemo } from 'react';
import { Tabs } from 'react-aria-components';

import { ComponentStack, Flex, Tab, TabList, TabPanel } from '@/components/core';
import { ModelPanelHeader } from '@/components/flow';
import { toModelId, useGetModel } from '@/hooks/api';
import { ModelConfigProvider } from '@/store';
import { convertToState } from '@/utils';

import { ModelSettings } from './ModelSettings';
import { Setup } from './Setup';

export type ModelPanelProps = {
  agentId: string;
  modelId: string;
};

const IModelPanel = ({ modelId }: ModelPanelProps) => {
  const { data: model } = useGetModel(toModelId(modelId));

  const fields = useMemo(() => convertToState(model?.fieldConfig), [model]);

  if (!model) return null;

  const metadata = model.metadata;
  const preprocessConfig = model.preprocessConfig;
  const postprocessConfig = model.postprocessConfig;

  return (
    <ModelConfigProvider
      modelId={modelId}
      fields={fields}
      metadata={metadata}
      preprocessConfig={preprocessConfig}
      postprocessConfig={postprocessConfig}
      llmVersion={model.llmVersion}
      confidenceVersion={model.confidenceVersion}
    >
      <Flex col className="w-full">
        <ModelPanelHeader className="border-b border-gray-200 p-4" modelId={modelId} />

        <Flex col className="grow">
          <ComponentStack>
            <Tabs className="group/model-panel">
              <TabList>
                <Tab className="grow justify-center" id="setup">
                  Setup
                </Tab>

                <Tab className="grow justify-center" id="advanced">
                  Settings
                </Tab>
              </TabList>

              <Flex col className="px-sm">
                <TabPanel id="setup">
                  <Setup />
                </TabPanel>

                <TabPanel id="advanced">
                  <ModelSettings />
                </TabPanel>
              </Flex>
            </Tabs>
          </ComponentStack>
        </Flex>
      </Flex>
    </ModelConfigProvider>
  );
};

export const ModelPanel = memo(IModelPanel) as typeof IModelPanel;
